import React, { useState, useEffect } from 'react';
import { Modal } from '../shared/modal/modal.component';
import { PrimaryButton, SecondaryButton, Text } from '@weave/design-system';
import { theme } from '@weave/theme-original';
import { css } from '@emotion/core';
import {
  bulkUserAccessApi,
  BulkOperationSummary,
} from '../../apis/bulk-user-access/bulk-user-access.api';
import {
  parseLocationInput,
  resolveLocationIdentifiers,
} from '../../apis/bulk-user-access/location-resolver';
import { RoleOption, RolesApiResponse } from '../../constants/user-roles';
import { useAlert } from '@weave/alert-system';
import { CustomAxios, getErrorMessage } from '../../redux/axios';
import { AxiosError } from 'axios';
import { useQueryClient } from 'react-query';

interface BulkManageModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  onSuccess?: () => void;
}

type OperationType = 'add' | 'remove';

export const BulkManageModal: React.FC<BulkManageModalProps> = ({
  isOpen,
  onClose,
  userId,
  onSuccess,
}) => {
  const alerts = useAlert();
  const queryClient = useQueryClient();
  const [operationType, setOperationType] = useState<OperationType>('add');
  const [selectedRoles, setSelectedRoles] = useState<number[]>([]);
  const [locationInput, setLocationInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [results, setResults] = useState<BulkOperationSummary | null>(null);
  const [roleSearchTerm, setRoleSearchTerm] = useState('');
  const [availableRoles, setAvailableRoles] = useState<RoleOption[]>([]);
  const [isLoadingRoles, setIsLoadingRoles] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchRoles();
    }
  }, [isOpen]);

  const fetchRoles = async () => {
    setIsLoadingRoles(true);
    try {
      const response = await CustomAxios.get<RolesApiResponse>(
        '/auth-api/v3/roles/categories'
      );
      const roles: RoleOption[] = response.data.roles
        .filter((role) => role.enabled)
        .map((role) => ({
          id: parseInt(role.roleId, 10),
          name: role.name,
          description: role.description || undefined,
          category: role.category,
          type: role.type,
        }))
        .sort((a, b) => {
          if (a.category && b.category && a.category !== b.category) {
            return a.category.localeCompare(b.category);
          }
          return a.name.localeCompare(b.name);
        });
      setAvailableRoles(roles);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      alerts.error('Failed to load roles: ' + errorMessage);
      setAvailableRoles([]);
    } finally {
      setIsLoadingRoles(false);
    }
  };

  const handleRoleToggle = (roleId: number) => {
    setSelectedRoles((prev) =>
      prev.includes(roleId) ? prev.filter((id) => id !== roleId) : [...prev, roleId]
    );
  };

  const handleSubmit = async () => {
    const identifiers = parseLocationInput(locationInput);

    if (identifiers.length === 0) {
      alerts.error('Please enter at least one location ID or slug');
      return;
    }

    if (operationType === 'add' && selectedRoles.length === 0) {
      alerts.error('Please select at least one role');
      return;
    }

    setIsProcessing(true);
    setResults(null);

    try {
      // Resolve slugs to UUIDs
      const resolutions = await resolveLocationIdentifiers(identifiers);

      // Check for resolution failures
      const failed = resolutions.filter((r) => !r.locationId);
      if (failed.length > 0) {
        const failedList = failed
          .slice(0, 5)
          .map((f) => `${f.original} (${f.error})`)
          .join(', ');
        alerts.error(
          `Failed to resolve ${failed.length} location${
            failed.length !== 1 ? 's' : ''
          }: ${failedList}${failed.length > 5 ? '...' : ''}`
        );
        setIsProcessing(false);
        return;
      }

      // Extract valid location IDs
      const validLocationIds = resolutions
        .filter((r) => r.locationId)
        .map((r) => r.locationId!);

      let summary: BulkOperationSummary;

      if (operationType === 'add') {
        summary = await bulkUserAccessApi.addRoleBulk(
          userId,
          validLocationIds,
          selectedRoles
        );
      } else {
        summary = await bulkUserAccessApi.deleteAccessBulk(userId, validLocationIds);
      }

      setResults(summary);

      if (summary.failed === 0) {
        alerts.success(
          `Successfully ${
            operationType === 'add' ? 'added roles to' : 'removed access from'
          } ${summary.succeeded} location${summary.succeeded !== 1 ? 's' : ''}`
        );
        // Invalidate react-query cache for user locations
        queryClient.invalidateQueries(['userLocations', userId]);
        if (onSuccess) {
          onSuccess();
        }
      } else {
        alerts.warning(
          `Completed with ${summary.succeeded} succeeded and ${summary.failed} failed`
        );
        // Even on partial success, invalidate cache to show updated locations
        queryClient.invalidateQueries(['userLocations', userId]);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An error occurred';
      alerts.error(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    setOperationType('add');
    setSelectedRoles([]);
    setLocationInput('');
    setResults(null);
    setRoleSearchTerm('');
    onClose();
  };

  const filteredRoles = availableRoles.filter(
    (role) =>
      role.name.toLowerCase().includes(roleSearchTerm.toLowerCase()) ||
      (role.description &&
        role.description.toLowerCase().includes(roleSearchTerm.toLowerCase())) ||
      (role.category &&
        role.category.toLowerCase().includes(roleSearchTerm.toLowerCase()))
  );

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleClose}
      title="Bulk Manage User Access"
      size="medium"
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: ${theme.spacing(3)};
        `}
      >
        {/* Operation Type */}
        <div>
          <Text
            weight="bold"
            as="label"
            css={css`
              display: block;
              margin-bottom: ${theme.spacing(1)};
            `}
          >
            Operation:
          </Text>
          <div
            css={css`
              display: flex;
              gap: ${theme.spacing(3)};
            `}
          >
            <label
              css={css`
                display: flex;
                align-items: center;
                gap: ${theme.spacing(1)};
                cursor: pointer;
              `}
            >
              <input
                type="radio"
                name="operationType"
                value="add"
                checked={operationType === 'add'}
                onChange={() => setOperationType('add')}
                disabled={isProcessing}
              />
              Add Roles
            </label>
            <label
              css={css`
                display: flex;
                align-items: center;
                gap: ${theme.spacing(1)};
                cursor: pointer;
              `}
            >
              <input
                type="radio"
                name="operationType"
                value="remove"
                checked={operationType === 'remove'}
                onChange={() => setOperationType('remove')}
                disabled={isProcessing}
              />
              Remove Access
            </label>
          </div>
        </div>

        {/* Role Selection */}
        {operationType === 'add' && (
          <div>
            <Text
              weight="bold"
              as="label"
              css={css`
                display: block;
                margin-bottom: ${theme.spacing(1)};
              `}
            >
              Select Roles:
            </Text>

            {/* Role Search */}
            <input
              type="text"
              placeholder="Search roles..."
              value={roleSearchTerm}
              onChange={(e) => setRoleSearchTerm(e.target.value)}
              disabled={isProcessing}
              css={css`
                width: 100%;
                padding: ${theme.spacing(1)} ${theme.spacing(1.5)};
                margin-bottom: ${theme.spacing(1)};
                border: 1px solid ${theme.colors.gray300};
                border-radius: ${theme.borderRadius.small};
                font-size: 0.875em;
                &:focus {
                  outline: none;
                  border-color: ${theme.colors.weaveBlue};
                }
                &::placeholder {
                  color: ${theme.colors.gray400};
                }
              `}
            />

            {/* Selected count */}
            {selectedRoles.length > 0 && (
              <div
                css={css`
                  font-size: 0.875em;
                  color: ${theme.colors.gray500};
                  margin-bottom: ${theme.spacing(1)};
                  padding: ${theme.spacing(0.5)} ${theme.spacing(1)};
                  background-color: ${theme.colors.gray100};
                  border-radius: ${theme.borderRadius.small};
                  display: inline-block;
                `}
              >
                {selectedRoles.length} role{selectedRoles.length !== 1 ? 's' : ''}{' '}
                selected
              </div>
            )}

            <div
              css={css`
                display: flex;
                flex-direction: column;
                border: 1px solid ${theme.colors.gray300};
                border-radius: ${theme.borderRadius.small};
                max-height: 300px;
                overflow-y: auto;
              `}
            >
              {isLoadingRoles ? (
                <div
                  css={css`
                    padding: ${theme.spacing(3)};
                    text-align: center;
                    color: ${theme.colors.gray500};
                    font-size: 0.875em;
                  `}
                >
                  Loading roles...
                </div>
              ) : filteredRoles.length === 0 ? (
                <div
                  css={css`
                    padding: ${theme.spacing(3)};
                    text-align: center;
                    color: ${theme.colors.gray500};
                    font-size: 0.875em;
                  `}
                >
                  {roleSearchTerm
                    ? `No roles found matching "${roleSearchTerm}"`
                    : 'No roles available'}
                </div>
              ) : (
                (() => {
                  let lastCategory: string | undefined = undefined;
                  return filteredRoles.map((role, index) => {
                    const showCategoryHeader =
                      role.category && role.category !== lastCategory;
                    lastCategory = role.category;

                    return (
                      <React.Fragment key={role.id}>
                        {showCategoryHeader && (
                          <div
                            css={css`
                              padding: ${theme.spacing(1)} ${theme.spacing(2)};
                              background-color: ${theme.colors.gray100};
                              font-size: 0.75em;
                              font-weight: 600;
                              text-transform: uppercase;
                              letter-spacing: 0.5px;
                              color: ${theme.colors.gray500};
                              border-bottom: 1px solid ${theme.colors.gray200};
                              position: sticky;
                              top: 0;
                              z-index: 1;
                            `}
                          >
                            {role.category}
                          </div>
                        )}
                        <label
                          css={css`
                            display: grid;
                            grid-template-columns: 24px 1fr;
                            gap: ${theme.spacing(1.5)};
                            align-items: start;
                            cursor: pointer;
                            padding: ${theme.spacing(1.5)} ${theme.spacing(2)};
                            border-bottom: 1px solid ${theme.colors.gray200};
                            transition: background-color 0.15s ease;
                            &:last-child {
                              border-bottom: none;
                            }
                            &:hover {
                              background-color: ${theme.colors.gray100};
                            }
                            ${selectedRoles.includes(role.id) &&
                            `
                              background-color: rgba(40, 202, 241, 0.05);
                              &:hover {
                                background-color: rgba(40, 202, 241, 0.1);
                              }
                            `}
                          `}
                        >
                          <input
                            type="checkbox"
                            checked={selectedRoles.includes(role.id)}
                            onChange={() => handleRoleToggle(role.id)}
                            disabled={isProcessing}
                            css={css`
                              width: 16px;
                              height: 16px;
                              margin-top: 3px;
                              cursor: pointer;
                            `}
                          />
                          <div
                            css={css`
                              min-width: 0;
                              line-height: 1.4;
                            `}
                          >
                            <div
                              css={css`
                                font-weight: 500;
                                font-size: 0.9375em;
                                margin-bottom: ${theme.spacing(0.25)};
                                color: ${theme.colors.gray600};
                              `}
                            >
                              {role.name}
                            </div>
                            {role.description && (
                              <div
                                css={css`
                                  font-size: 0.8125em;
                                  color: ${theme.colors.gray500};
                                  line-height: 1.4;
                                `}
                              >
                                {role.description}
                              </div>
                            )}
                          </div>
                        </label>
                      </React.Fragment>
                    );
                  });
                })()
              )}
            </div>
          </div>
        )}

        {/* Location IDs Input */}
        <div>
          <label
            htmlFor="location-input"
            css={css`
              display: block;
              margin-bottom: ${theme.spacing(1)};
            `}
          >
            <Text weight="bold">Location IDs or Slugs (comma or newline separated):</Text>
          </label>
          <textarea
            id="location-input"
            value={locationInput}
            onChange={(e) => setLocationInput(e.target.value)}
            disabled={isProcessing}
            placeholder="e.g., weave-hq, my-practice, dfbbf8cd-ac87-42be-87fc-2c243272f122"
            css={css`
              width: 100%;
              min-height: 120px;
              padding: ${theme.spacing(1.5)};
              border: 1px solid ${theme.colors.gray300};
              border-radius: ${theme.borderRadius.small};
              font-family: monospace;
              font-size: 0.875em;
              resize: vertical;
              &:focus {
                outline: none;
                border-color: ${theme.colors.weaveBlue};
              }
            `}
          />
        </div>

        {/* Results */}
        {results && (
          <div
            css={css`
              padding: ${theme.spacing(2.5)};
              background-color: ${results.failed === 0 ? '#d4edda' : '#fff3cd'};
              border-radius: ${theme.borderRadius.small};
              border: 1px solid ${results.failed === 0 ? '#c3e6cb' : '#ffeaa7'};
            `}
          >
            <Text weight="bold">Results:</Text>
            <div
              css={css`
                margin-top: ${theme.spacing(1.5)};
                display: flex;
                flex-direction: column;
                gap: ${theme.spacing(0.5)};
              `}
            >
              <div>Total: {results.total}</div>
              <div
                css={css`
                  color: ${theme.colors.success};
                `}
              >
                Succeeded: {results.succeeded}
              </div>
              {results.failed > 0 && (
                <div
                  css={css`
                    color: ${theme.colors.error};
                  `}
                >
                  Failed: {results.failed}
                </div>
              )}
            </div>
            {results.failed > 0 && (
              <details
                css={css`
                  margin-top: ${theme.spacing(1.5)};
                `}
              >
                <summary
                  css={css`
                    cursor: pointer;
                    font-weight: 500;
                    padding: ${theme.spacing(0.5)} 0;
                    &:hover {
                      color: ${theme.colors.weaveBlue};
                    }
                  `}
                >
                  View Errors
                </summary>
                <div
                  css={css`
                    max-height: 150px;
                    overflow-y: auto;
                    margin-top: ${theme.spacing(1)};
                    padding: ${theme.spacing(1)};
                    background-color: rgba(255, 255, 255, 0.5);
                    border-radius: ${theme.borderRadius.small};
                    font-size: 0.875em;
                  `}
                >
                  {results.results
                    .filter((r) => !r.success)
                    .map((r, idx) => (
                      <div
                        key={idx}
                        css={css`
                          margin-bottom: ${theme.spacing(0.5)};
                          padding: ${theme.spacing(0.5)};
                          &:not(:last-child) {
                            border-bottom: 1px solid rgba(0, 0, 0, 0.1);
                          }
                        `}
                      >
                        <strong>{r.locationId}</strong>: {r.error}
                      </div>
                    ))}
                </div>
              </details>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div
          css={css`
            display: flex;
            gap: ${theme.spacing(2)};
            justify-content: flex-end;
            padding-top: ${theme.spacing(1)};
            border-top: 1px solid ${theme.colors.gray200};
          `}
        >
          <SecondaryButton onClick={handleClose} disabled={isProcessing}>
            {results ? 'Close' : 'Cancel'}
          </SecondaryButton>
          {!results && (
            <PrimaryButton onClick={handleSubmit} disabled={isProcessing}>
              {isProcessing
                ? 'Processing...'
                : operationType === 'add'
                ? 'Add Roles'
                : 'Remove Access'}
            </PrimaryButton>
          )}
        </div>

        {isProcessing && (
          <div
            css={css`
              text-align: center;
              color: ${theme.colors.gray500};
            `}
          >
            <Text>Processing locations... This may take a moment.</Text>
          </div>
        )}
      </div>
    </Modal>
  );
};
